import React, { useState } from "react";
import { Question } from "../survey-types";
import QuestionsList from "../QuestionsList";
import SingleQuestionEditor from "./SingleQuestionEditor";
import { Plus } from "@/components/Plus";

function generateUniqueID() {
  return Math.random().toString(36).substring(2, 9);
}

interface QuestionnaireEditorProps {
  questions: Question[];
  setQuestions: (questions: Question[]) => void;
}

const QuestionnaireEditor: React.FC<QuestionnaireEditorProps> = ({
  questions,
  setQuestions,
}) => {
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);

  const handleAddQuestion = (questionType: string) => {
    let newQuestion: Question;
    const base = {
      id: generateUniqueID(),
      text: "",
      required: false,
    };

    switch (questionType) {
      case "multiple-choice":
        newQuestion = {
          type: "multiple-choice",
          choices: [],
          ...base,
        };
        break;
      case "text":
        newQuestion = {
          type: "text",
          ...base,
        };
        break;
      case "opinion-scale":
        newQuestion = {
          type: "opinion-scale",
          min: 1,
          max: 10,
          minLabel: "Strongly Disagree",
          maxLabel: "Strongly Agree",
          ...base,
        };
        break;
      case "ranking":
        newQuestion = {
          type: "ranking",
          choices: [],
          ...base,
        };
        break;
      case "matrix":
        newQuestion = {
          type: "matrix",
          rows: [],
          columns: [],
          multiple: false,
          ...base,
        };
        break;
      default:
        throw new Error("Invalid question type: " + questionType);
    }

    const nQuestions = questions.length;
    setQuestions([...questions, newQuestion]);
    setSelectedQuestionIndex(nQuestions);
  };

  const handleSaveQuestion = (question: Question) => {
    setQuestions(
      questions.map((q, index) =>
        index === selectedQuestionIndex ? question : q
      )
    );
  };

  const handleDeleteQuestion = (index: number) => {
    const nQuestions = questions.length - 1;
    setQuestions(questions.filter((_, i) => i !== index));
    setSelectedQuestionIndex(Math.min(index, nQuestions - 1));
  };

  return (
    <div className="flex w-full">
      <div className="flex-none w-64 flex-col sm:flex-row">
        <QuestionsList
          questions={questions}
          selectedQuestionIndex={selectedQuestionIndex}
          setSelectedQuestionIndex={setSelectedQuestionIndex}
        />

        <button
          // className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-4"
          className="text-white hover:text-slate-400 text-sm font-semibold h-12 px-6 w-full flex items-center justify-center"
          onClick={() => handleAddQuestion("multiple-choice")}
        >
          <Plus />
          Add Question
        </button>
      </div>
      <div className="flex-1 pl-8">
        {questions[selectedQuestionIndex] && (
          <>
            <SingleQuestionEditor
              question={questions[selectedQuestionIndex]}
              onChange={handleSaveQuestion}
              onDelete={() => handleDeleteQuestion(selectedQuestionIndex)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionnaireEditor;
